import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { Adg4Button } from "@/components/adg4/button/src/button";
import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint/use-touchpoint/index";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { DEFAULT_VALUES } from "@/libraries/web-framework/src/localization/values";
import { Row } from "@/libraries/wpl/foundations/src/grid";

const Hero = () => {
    const intl = useIntl();
    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-product-discovery"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_PRODUCT_DISCOVERY,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });
    return (
        <div css={container}>
            <Row layout="8">
                <div css={heroContainer}>
                    <h1 className="superhero" css={heading}>
                        {intl.formatMessage(
                            {
                                id: "JiraProductDiscoveryPage.hero.heading.super",
                                defaultMessage:
                                    "From ideas to <em>impact</em>, build what matters"
                            },
                            {
                                ...DEFAULT_VALUES,
                                em: (text: ReactNode) => {
                                    return <span css={impact}>{text}</span>;
                                }
                            }
                        )}
                    </h1>
                    <p className="body-lg">
                        {intl.formatMessage({
                            id: "JiraProductDiscoveryPage.hero.subtitle",
                            defaultMessage:
                                "Capture insights, prioritize ideas, and build roadmaps - all in Jira"
                        })}
                    </p>
                    <Adg4Button
                        css={button}
                        href={signupLink}
                        onClick={openCrossFlow}
                        variant="primary"
                        dataEventProps={{
                            "data-event": "clicked",
                            "data-event-container": "heroSection",
                            "data-event-component": "linkButton",
                            "data-product-key": "jiraProductDiscovery",
                            "data-label-english": "Get it free",
                            "data-track-event":
                                "jiraProductDiscovery.get-it-free-button-clicked"
                        }}
                    >
                        {intl.formatMessage({
                            id: "JiraProductDiscoveryPage.hero.cta.label",
                            defaultMessage: "Get it free"
                        })}
                    </Adg4Button>
                </div>
            </Row>
        </div>
    );
};

export default Hero;

const container = css({
    overflowX: "hidden"
});

const heroContainer = css({
    textAlign: "center",
    paddingBlock: `${token("space.200")} ${token("space.400")}`,
    [media.above.md]: {
        paddingBlockStart: token("space.800")
    }
});
const heading = css({
    "position": "relative",
    "::before": {
        zIndex: -1,
        position: "absolute",
        content: "",
        inset: "0 -100px -10px -82px",
        backgroundImage: `
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/0moki2v04l03l515673048kkt0306k16.svg),
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/8j83r8541i0dhtkvw8b35h7504442835.svg)
            `,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "50px calc(100% - 30px), calc(100% - 75px) 100%",
        [media.below.sm]: {
            backgroundSize: "60px 97px, 100px 40px"
        },
        [media.above.sm]: {
            backgroundPosition: "0% calc(100% - 30px), calc(100% - 30px) 100%"
        }
    }
});
const impact = css({
    "position": "relative",
    "::before": {
        position: "absolute",
        content: "",
        inset: "0",
        backgroundImage: `
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/xc73vmba7x1ys5y8626583550o4r7mj7.svg)
            `,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "100% 100%",
        transform: "translate(48px,-20px) scale(.675)",
        [media.only.xs]: {
            transform: "translate(48px,-28px) scale(.675)"
        },
        [media.only.sm]: {
            transform: "translate(48px,-28px) scale(.75)"
        },
        [media.above.md]: {
            transform: "translate(44px,-36px) scale(.875)"
        }
    }
});
const button = css({
    marginBlockStart: token("space.200")
});
